import styled from 'styled-components';
import { alpha } from '@mui/material';
import { DataGridProps, DataGrid as MUIDataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

const DataGrid: React.FC<DataGridProps> = (props) => {
  const { t } = useTranslation();
  const columns = props.columns.map((column) => ({
    ...column,
    // Parenthesised deliberately: `??` binds looser than `===`, so without the parentheses this read
    // `(column.minWidth ?? (column.flex === 1)) ? 100 : 200`, which threw away any declared minWidth
    // and forced every flex < 1 column to 200px.
    minWidth: column.minWidth ?? (column.flex === 1 ? 100 : 200),
  }));
  const localeText = {
    ...props.localeText,
    MuiTablePagination: {
      labelRowsPerPage: t('dataGrid.rowsPerPage'),
      ...props.localeText?.MuiTablePagination,
    },
  };
  return <StyledDataGrid {...props} columns={columns} localeText={localeText} />;
};

const StyledDataGrid = styled(MUIDataGrid)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  /* The grid spreads the theme's body2 onto its root, and body2 is text.secondary gray. */
  color: ${({ theme }) => theme.palette.text.primary};

  /* The row tint marks the clicked row, so the focus box is dropped for mouse clicks but kept for the keyboard. */
  .MuiDataGrid-cell:focus,
  .MuiDataGrid-cell:focus-within,
  .MuiDataGrid-columnHeader:focus,
  .MuiDataGrid-columnHeader:focus-within {
    outline: none;
  }

  .MuiDataGrid-cell:focus-visible,
  .MuiDataGrid-columnHeader:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.main};
    outline-offset: -2px;
  }

  .cell-link {
    color: ${({ theme }) => theme.palette.primary.main};

    &:hover {
      color: ${({ theme }) => theme.palette.primary.dark};
    }
  }

  /* Which row the details panel is showing. */
  .grid-row-open {
    background-color: ${({ theme }) => alpha(theme.palette.primary.light, 0.08)};

    &:hover {
      background-color: ${({ theme }) => alpha(theme.palette.primary.light, 0.12)};
    }
  }

  .grid-row-not-active {
    background-color: ${({ theme }) => theme.palette.error.light};

    &:hover {
      background-color: ${({ theme }) => theme.palette.error.dark};
    }
  }

  .MuiDataGrid-row {
    cursor: pointer;

    /* A tint, not a solid fill: green links, chips and checkboxes in the row must stay readable. */
    &.Mui-selected {
      background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};

      &:hover {
        background-color: ${({ theme }) => alpha(theme.palette.primary.main, 0.16)};
      }

      &.grid-row-not-active {
        background-color: ${({ theme }) => theme.palette.error.dark};

        &:hover {
          background-color: ${({ theme }) => theme.palette.error.dark};
        }
      }
    }
  }
`;

export default DataGrid;
